import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, ElementRef, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {NgOptimizedImage, NgStyle} from '@angular/common';
import {CdkDrag, CdkDragHandle, Point} from '@angular/cdk/drag-drop';
import {
  OriginalPapayaScanView,
  OriginalPapayaViewerSlice,
} from '../../../dashboard-study-papaya/types/dashboard-study-papaya-original.types';
import {DashboardStudyViewerControllerService} from '../../../dashboard-study/services/dashboard-study-viewer-controller/dashboard-study-viewer-controller.service';
import {DashboardStudyViewerZoomedControlsSliceComponent} from '../dashboard-study-viewer-zoomed-controls-slice/dashboard-study-viewer-zoomed-controls-slice.component';
import {DashboardStudyViewerZoomedControlsZoomComponent} from '../dashboard-study-viewer-zoomed-controls-zoom/dashboard-study-viewer-zoomed-controls-zoom.component';
import {
  DashboardStudyViewerZoomedControlsOpacityComponent
} from '../dashboard-study-viewer-zoomed-controls-opacity/dashboard-study-viewer-zoomed-controls-opacity.component';

@Component({
  selector: 'app-dashboard-study-viewer-zoomed-controls',
  templateUrl: './dashboard-study-viewer-zoomed-controls.component.html',
  styleUrls: ['./dashboard-study-viewer-zoomed-controls.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkDrag,
    CdkDragHandle,
    NgOptimizedImage,
    DashboardStudyViewerZoomedControlsSliceComponent,
    NgStyle,
    DashboardStudyViewerZoomedControlsZoomComponent,
    FormsModule,
    ReactiveFormsModule,
    DashboardStudyViewerZoomedControlsOpacityComponent,
  ],
})
export class DashboardStudyViewerZoomedControlsComponent implements OnInit, AfterViewInit {
  @ViewChild('thumbnailCanvas') thumbnailCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('viewerZone') public viewerZone?: ElementRef<HTMLDivElement>;

  /** The form control for the zoom level. */
  public readonly zoomControl = new FormControl<number>(1);

  /**
   * The size of the parent container (place where the thumbnail is rendered).
   * The container zone is used to contain the viewer square and the thumbnail.
   * It scales to the current papaya viewer effective (after zoom) size.
   */
  public readonly defaultContainerSize = 200;

  public parentContainerHeight = this.defaultContainerSize;
  public parentContainerWidth = this.defaultContainerSize;

  /**
   * The default size of the viewer zone (blue transparent square).
   * This is the size of the square that is used to display which part of the image is currently displayed in the papaya viewer.
   */
  public readonly defaultViewerZoneSize = 200;

  /**
   * The position of the square in the viewer zone.
   * Used for programmatically dragging the square, as changes to the variable apply changes to the square position.
   */
  public dragPosition: Point = {x: 0, y: 0};

  /** The current shift on X axis of the square in the viewer zone. */
  public shiftX = 0;

  /** The current shift on Y axis of the square in the viewer zone. */
  public shiftY = 0;

  /** The current shift on Z axis of the square in the viewer zone. */
  public shiftZ = 0;

  /** The current size of the viewer zone. */
  public viewerSize = 200;

  /** The current zoom level. */
  public zoom = 1;

  /** The currently selected slice. */
  public selectedSlice = OriginalPapayaViewerSlice.Axial;

  constructor(private cd: ChangeDetectorRef, private studyViewerController: DashboardStudyViewerControllerService) {
    // Redraw the thumbnail image each time the view inspect point is changed.
    effect(() => {
      const mouseDown = this.studyViewerController.papayaController.mouseDown();
      if (!mouseDown) {
        this.drawThumbnailImage();
      }
    });
  }

  ngOnInit(): void {
    // On each zoom level change, apply the zoom to the viewer zone.
    this.zoomControl.valueChanges.subscribe(zoom => {
      // Update the zoom level.
      this.zoom = zoom;

      // Update the thumbnail image.
      this.updateThumbnailSize();
      this.drawThumbnailImage();

      // Calculate and update the viewer size and shifts.
      this.updateViewerSizeAndShifts();

      // Update the related view in the papaya viewer.
      this.updatePapayaViewer();
    });
  }

  ngAfterViewInit(): void {
    this.studyViewerController.papayaController.drawMainSliceOnCustomCanvas(this.thumbnailCanvas.nativeElement);
  }

  /**
   * Handles the drag of the viewer zone by updating the shift values.
   * @param viewerZone The viewer zone element.
   * @param thumbnail The thumbnail element (parent container).
   */
  public viewerZoneMoved(viewerZone: HTMLDivElement, thumbnail: HTMLDivElement) {
    // Get the bounding client rectangle of the thumbnail and the viewer zone.
    const thumbnailRect = thumbnail.getBoundingClientRect();
    const viewerZoneRect = viewerZone.getBoundingClientRect();

    // Calculate the shift values.
    this.shiftX = Math.abs(thumbnailRect.x - viewerZoneRect.x);
    this.shiftY = Math.abs(thumbnailRect.y - viewerZoneRect.y);

    // Update the related view in the papaya viewer.
    this.updatePapayaViewer();
  }

  /**
   * Applies the zoom to the viewer zone.
   * Requires recalculating the shift values.
   */
  public updateViewerSizeAndShifts(): void {
    // Get the viewer zone element.
    const viewerZone = this.viewerZone.nativeElement;

    // If the viewer zone is not defined, do nothing.
    if (!viewerZone) {
      return;
    }

    // Get the bounding client rectangle of the viewer zone.
    const boundingClientRect = viewerZone.getBoundingClientRect();

    // Calculate the previous width and height.
    const previousSize = boundingClientRect.width;

    // Calculate the new width and height based on the zoom.
    const newSize = this.defaultViewerZoneSize / this.zoom;

    // Calculate the shift caused by the zoom.
    const requiredShift = (previousSize - newSize) / 2;

    // Calculate the new shift values as a sum of the previous shift and the shift that is caused by the zoom.
    const updatedXShift = requiredShift + this.shiftX;
    const updatedYShift = requiredShift + this.shiftY;
    const updatedZShift = requiredShift + this.shiftZ;

    // The square should not be able to go outside the thumbnail on the left or right side.
    this.shiftX = this.applyLimitsToShift(newSize, updatedXShift, this.parentContainerWidth);

    // The square should not be able to go outside the thumbnail on the top or bottom side.
    this.shiftY = this.applyLimitsToShift(newSize, updatedYShift, this.parentContainerHeight);

    // The square should not be able to go outside the thumbnail image shallow or deep.
    this.shiftZ = this.applyLimitsToShift(newSize, updatedZShift, this.defaultContainerSize);

    // Update the local variables.
    this.viewerSize = newSize;

    // Update the square position by applying the new shift values.
    this.dragPosition = {x: this.shiftX, y: this.shiftY};

    // Update the viewer zone styles.
    viewerZone.style.width = `${this.viewerSize}px`;
    viewerZone.style.height = `${this.viewerSize}px`;
  }

  /**
   * Redraws the papaya viewer based on the current shift and zoom values.
   */
  public updatePapayaViewer(): void {
    // Calculate the maximal possible shift values.
    const maximalShiftX = this.parentContainerWidth - this.viewerSize;
    const maximalShiftY = this.parentContainerHeight - this.viewerSize;
    const maximalShiftZ = this.defaultContainerSize - this.viewerSize;

    // Calculate the percentage of the shift values.
    const shiftXCoefficient = maximalShiftX <= 0 ? 1 : this.shiftX / maximalShiftX;
    const shiftYCoefficient = maximalShiftY <= 0 ? 1 : this.shiftY / maximalShiftY;
    const shiftZCoefficient = maximalShiftZ <= 0 ? 1 : this.shiftZ / maximalShiftZ;

    // Get the dimensions based on the currently selected slice.
    const [xDim, yDim, zDim] = this.studyViewerController.papayaController.getDimensionsBasedOnCurrentSlice();

    // Papaya uses some approach where the farther the square is from the center, the bigger the shift value should be.
    // Apply this approach by taking the percentage of the viewer size.
    // Convert the percentage to the papaya dimension sizes.
    const panXShiftNeeded = ((this.shiftX + this.viewerSize * shiftXCoefficient) * xDim) / this.parentContainerWidth;
    const panYShiftNeeded = ((this.shiftY + this.viewerSize * shiftYCoefficient) * yDim) / this.parentContainerHeight;
    const panZShiftNeeded = ((this.shiftZ + this.viewerSize * shiftZCoefficient) * zDim) / this.defaultContainerSize;

    this.studyViewerController.papayaController.setZoomFactor(this.zoom);

    // Extract the currently active slice image.
    const currentlyActiveSliceImage = (
      this.studyViewerController.papayaController.currentPapayaInstance.viewer.mainImage as OriginalPapayaScanView
    ).sliceDirection;

    // Set the pan location to the papaya viewer and trigger redraw.
    this.studyViewerController.papayaController.setExactPanLocation(
      -panXShiftNeeded,
      -panYShiftNeeded,
      -panZShiftNeeded,
      currentlyActiveSliceImage
    );
  }

  /**
   * Swaps the slice in the papaya viewer.
   */
  public swapSlice(sliceSelected: OriginalPapayaViewerSlice): void {
    // Calculate the shift switch depending on the previously selected slice and the currently selected slice.
    switch (this.selectedSlice) {
      case OriginalPapayaViewerSlice.Axial: {
        // Axial <-> Coronal: keep X, swap Y and Z
        if (sliceSelected === OriginalPapayaViewerSlice.Coronal) {
          this.updateShiftsKeepX();
          // Axial <-> Sagittal: clockwise or counter-clockwise
        } else if (sliceSelected === OriginalPapayaViewerSlice.Sagittal) {
          this.updateShitsClockwise();
        }
        break;
      }

      case OriginalPapayaViewerSlice.Coronal: {
        // Axial <-> Coronal: keep X, swap Y and Z
        if (sliceSelected === OriginalPapayaViewerSlice.Axial) {
          this.updateShiftsKeepX();
          // Sagittal <-> Coronal: keep Y, swap X and Z
        } else if (sliceSelected === OriginalPapayaViewerSlice.Sagittal) {
          this.updateShiftsKeepY();
        }
        break;
      }

      case OriginalPapayaViewerSlice.Sagittal: {
        // Axial <-> Sagittal: clockwise or counter-clockwise
        if (sliceSelected === OriginalPapayaViewerSlice.Axial) {
          this.updateShiftsCounterClockwise();
          // Sagittal <-> Coronal: keep Y, swap X and Z
        } else if (sliceSelected === OriginalPapayaViewerSlice.Coronal) {
          this.updateShiftsKeepY();
        }
        break;
      }
    }

    // Update the selected slice.
    this.selectedSlice = sliceSelected;

    // Update the thumbnail image.
    this.updateThumbnailSize();
    this.drawThumbnailImage();

    // Update the square position by applying the new shift values.
    this.dragPosition = {x: this.shiftX, y: this.shiftY};

    // Sometimes the related thumbnail is not updated, so force the change detection.
    this.cd.detectChanges();
  }

  public moveViewerToClickedPosition(event: MouseEvent) {
    // Calculate the shift values as a coordinate of the click (center of the viewer) minus the half of the viewer size.
    this.shiftX = event.offsetX - this.viewerSize / 2;
    this.shiftY = event.offsetY - this.viewerSize / 2;

    // Update the square position by applying the new shift values.
    this.updateViewerSizeAndShifts();

    // Update the related view in the papaya viewer.
    this.updatePapayaViewer();
  }

  /**
   * Exits the zoomed view.
   * Resets the main slice and zoom.
   */
  public exitZoomedView(): void {
    this.studyViewerController.papayaController.reorderViewsToSpecificPosition(OriginalPapayaViewerSlice.Axial, true);
    this.studyViewerController.papayaController.setZoomFactor(1);
    this.studyViewerController.isZoomedView.set(false);
  }

  /**
   * Draws the thumbnail image on the canvas.
   */
  private drawThumbnailImage(): void {
    // If the thumbnail canvas is not defined, do nothing.
    if (!this.thumbnailCanvas || !this.thumbnailCanvas.nativeElement) {
      return;
    }

    this.studyViewerController.papayaController.drawMainSliceOnCustomCanvas(this.thumbnailCanvas.nativeElement);
  }

  /**
   * Updates the thumbnail size based on the current zoom level and effective viewer size.
   */
  private updateThumbnailSize(): void {
    const mainSlice: OriginalPapayaScanView = this.studyViewerController.papayaController.currentPapayaInstance.viewer
      .mainImage as OriginalPapayaScanView;

    // Get the base viewer size from the main slice dimensions.
    const baseViewerSize = mainSlice.screenDim;
    const zoomedImageWidth = mainSlice.canvasMain.width * Math.abs(mainSlice.finalTransform[0][0]);
    const zoomedImageHeight = mainSlice.canvasMain.height * Math.abs(mainSlice.finalTransform[1][1]);

    // At current zoom, the image appears larger but black areas stay the same.
    const renderedWidth = zoomedImageWidth / this.zoom;
    const renderedHeight = zoomedImageHeight / this.zoom;

    // Calculate black areas (constant regardless of zoom).
    const blackWidth = baseViewerSize - renderedWidth;
    const blackHeight = baseViewerSize - renderedHeight;

    // Total "effective" viewer size at current zoom.
    const effectiveViewerWidth = blackWidth + zoomedImageWidth;
    const effectiveViewerHeight = blackHeight + zoomedImageHeight;

    // Now calculate thumbnail size to match these ratios.
    const baseThumbnailSize = this.defaultViewerZoneSize;

    // Calculate the scale factors for the thumbnail based on the effective viewer size.
    const thumbnailScaleWidth = effectiveViewerWidth / (baseViewerSize * this.zoom);
    const thumbnailScaleHeight = effectiveViewerHeight / (baseViewerSize * this.zoom);

    // Apply the scale factors to the base thumbnail size.
    this.parentContainerWidth = Math.floor(baseThumbnailSize * thumbnailScaleWidth);
    this.parentContainerHeight = Math.floor(baseThumbnailSize * thumbnailScaleHeight);
  }

  /**
   * Applies the limits to the shift value.
   */
  private applyLimitsToShift(viewerSize: number, shiftToApply: number, limit: number): number {
    // The square should not be able to go outside the image bigger border.
    const applyBiggerLimit = shiftToApply + viewerSize > limit ? limit - viewerSize : shiftToApply;

    // The square should not be able to go outside the thumbnail image lower border.
    return applyBiggerLimit < 0 ? 0 : applyBiggerLimit;
  }

  /**
   * Updates the shift values to keep the X axis.
   */
  private updateShiftsKeepX(): void {
    const temp = this.shiftY;
    this.shiftY = this.shiftZ;
    this.shiftZ = temp;
  }

  /**
   * Updates the shift values to keep the Y axis.
   */
  private updateShiftsKeepY(): void {
    const temp = this.shiftX;
    this.shiftX = this.shiftZ;
    this.shiftZ = temp;
  }

  /**
   * Updates the shift values clockwise: X -> Y, Y -> Z, Z -> X.
   */
  private updateShitsClockwise(): void {
    const temp = this.shiftX;
    this.shiftX = this.shiftY;
    this.shiftY = this.shiftZ;
    this.shiftZ = temp;
  }

  /**
   * Updates the shift values counter-clockwise: X -> Z, Y -> X, Z -> Y.
   */
  private updateShiftsCounterClockwise(): void {
    const temp = this.shiftX;
    this.shiftX = this.shiftZ;
    this.shiftZ = this.shiftY;
    this.shiftY = temp;
  }
}
